import { useRef, useState, type AnimationEvent } from 'react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { ArrowUpRightIcon, MoreVerticalIcon, PencilIcon, Trash2Icon } from 'lucide-react';
import { ToolBadge } from '@/shared/ui/ToolBadge';
import type { Agent } from '@/entities/agent';
import { microInteraction } from '@/shared/ui/MicroInteractions';
import { CustomAgentChip } from '@/shared/ui/CustomAgentChip';
import { useTranslation } from 'react-i18next';

interface FilledShortcutSlotProps {
  slotIndex: number;
  agentId: string;
  agent: Agent | null;
  isRemoving: boolean;
  onLaunch: () => void;
  onEdit: () => void;
  onRemove: () => void;
  /** Plays the settle-in entrance once, right after this slot was confirmed. */
  justSaved?: boolean;
  onEnterAnimationEnd?: () => void;
  /** Plays the shrink-and-fade exit while the removal is still pending. */
  isExiting?: boolean;
}

export function FilledShortcutSlot({
  slotIndex,
  agentId,
  agent,
  isRemoving,
  onLaunch,
  onEdit,
  onRemove,
  justSaved = false,
  onEnterAnimationEnd,
  isExiting = false,
}: FilledShortcutSlotProps) {
  const { t } = useTranslation('springboard');

  const isDisabled = !agent;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // The icon and the title animate in too, and their `animationend` bubbles
  // here — reacting to a child's would end the tile's own longer entrance
  // partway through, so only the tile's own animation counts.
  const handleAnimationEnd = (event: AnimationEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (justSaved && !isExiting) onEnterAnimationEnd?.();
  };

  return (
    <div
      data-testid={`shortcut-slot-${slotIndex}-filled`}
      onAnimationEnd={handleAnimationEnd}
      className={[
        'group relative flex flex-col h-full w-full items-start justify-between gap-3 rounded-lg border border-transparent bg-card p-4 transition-[border-color] duration-base ease-out',
        isDisabled ? 'opacity-50' : 'hover:border-border',
        // `animate-[...]` sets one CSS property, so exit and entrance can't
        // both apply — removal (rare to coincide, but possible right after a
        // save) always wins visually.
        isExiting
          ? `pointer-events-none ${microInteraction.shortcutOut}`
          : justSaved
            ? microInteraction.shortcutPopIn
            : '',
      ].join(' ')}
    >
      <ToolBadge
        toolId={agentId}
        category={agent?.tag}
        className={microInteraction.shortcutContentIn}
      />
      <span
        className={`flex w-full min-w-0 items-center gap-xs ${microInteraction.shortcutContentIn}`}
      >
        <span className="min-w-0 truncate type-heading-sm text-foreground">
          {agent?.title ?? t('dashboard.filledShortcutSlot.toolUnavailable')}
        </span>
        {agent?.visibility && <CustomAgentChip visibility={agent.visibility} />}
      </span>
      <div
        role="presentation"
        className={`absolute top-4 right-4 flex items-center gap-1 transition-opacity duration-base ease-out ${
          isDropdownOpen
            ? 'opacity-100'
            : isDisabled
              ? 'opacity-0'
              : 'opacity-0 group-hover:opacity-100 focus-within:opacity-100'
        }`}
      >
        {!isDisabled && (
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="mini"
                  className="size-8 rounded-md p-0"
                  aria-label={t('dashboard.filledShortcutSlot.launch')}
                  onClick={onLaunch}
                >
                  <ArrowUpRightIcon className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {t('dashboard.filledShortcutSlot.launch')}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        <DropdownMenu onOpenChange={setIsDropdownOpen}>
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button
                    ref={triggerRef}
                    variant="ghost"
                    size="mini"
                    className="size-8 rounded-md p-0"
                    aria-label={t('dashboard.filledShortcutSlot.shortcutActions')}
                  >
                    <MoreVerticalIcon className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {t('dashboard.filledShortcutSlot.shortcutActions')}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                onEdit();
              }}
              className="cursor-pointer text-foreground focus:text-foreground"
            >
              <PencilIcon className="mr-2 size-4" />
              {t('dashboard.filledShortcutSlot.edit')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                onRemove();
              }}
              disabled={isRemoving}
              className="cursor-pointer text-foreground focus:text-foreground"
            >
              <Trash2Icon className="mr-2 size-4" />
              {t('dashboard.filledShortcutSlot.remove')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
